import borwserLang from './globalFunc/borwserLang.js'
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from './constant.js'

const DEFAULT_HOME_PATH = '/home'
const LOCALE_PARAM_KEY = 'locale'

/**
 * 标准化路径格式
 * @param {string} path - 原始路径
 * @returns {string} 标准化后的路径
 * @example '/path//to/' -> '/path/to'
 */
const normalizePath = (path = '/') => {
  if (!path) {
    return '/'
  }
  if (path === '/') {
    return path
  }
  const value = path.startsWith('/') ? path : `/${path}`
  return value.replace(/\/{2,}/g, '/').replace(/\/$/, '') || '/'
}

/**
 * 获取路径的分段数组
 * @param {string} path - 路径
 * @returns {string[]} 路径分段数组
 * @example '/en_us/home' -> ['en_us', 'home']
 */
const getPathSegments = (path = '/') => {
  return normalizePath(path).split('/').filter(Boolean)
}

/**
 * 检查是否为支持的语言码
 * @param {string} locale - 语言码
 * @returns {boolean} 是否支持
 * @example 'en_us' -> true, 'fr_fr' -> false
 */
const isSupportedLocale = (locale = '') => SUPPORTED_LOCALES.includes(locale)

/**
 * 从路径中识别并提取语言码
 * @param {string} path - 路径
 * @returns {string} 提取到的语言码，未匹配则返回空字符串
 * @example '/en_us/home' -> 'en_us', '/home' -> ''
 */
const getLocaleFromPath = (path = '/') => {
  const [first] = getPathSegments(path)
  return isSupportedLocale(first) ? first : ''
}

/**
 * 移除路径中的语言码前缀
 * @param {string} path - 路径
 * @returns {string} 移除前缀后的路径
 * @example '/en_us/home' -> '/home', '/zh_cn' -> '/'
 */
const stripLocalePrefix = (path = '/') => {
  const normalizedPath = normalizePath(path)
  const locale = getLocaleFromPath(normalizedPath)

  if (!locale) {
    return normalizedPath
  }

  const segments = getPathSegments(normalizedPath).slice(1)
  if (!segments.length) {
    return '/'
  }
  return `/${segments.join('/')}`
}

/**
 * 获取标准化的业务路径（不含语言前缀，处理首页根路径）
 * @param {string} path - 路径
 * @returns {string} 业务路径
 * @example '/en_us/' -> '/home', '/en_us/trade' -> '/trade'
 */
const normalizeBusinessPath = (path = '/') => {
  const businessPath = normalizePath(stripLocalePrefix(path))
  return businessPath === '/' ? DEFAULT_HOME_PATH : businessPath
}

/**
 * 为业务路径添加语言码前缀
 * @param {string} path - 业务路径
 * @param {string} locale - 语言码
 * @returns {string} 带有语言前缀的完整路径
 * @example ('/home', 'en_us') -> '/en_us/home'
 */
const addLocalePrefix = (path = '/', locale = DEFAULT_LOCALE) => {
  const safeLocale = isSupportedLocale(locale) ? locale : DEFAULT_LOCALE
  const businessPath = normalizeBusinessPath(path)
  return `/${safeLocale}${businessPath}`
}

/**
 * 判断路径是否包含有效的语言前缀
 * @param {string} path - 路径
 * @returns {boolean} 是否包含
 * @example '/en_us/home' -> true, '/home' -> false
 */
const isLocalePath = (path = '/') => Boolean(getLocaleFromPath(path))

/**
 * 获取对应的业务路径
 * @param {string} path - 路径
 * @returns {string} 业务路径
 * @example '/en_us/trade' -> '/trade'
 */
const getBusinessPath = (path = '/') => normalizeBusinessPath(path)

/**
 * 从查询参数中移除 locale 字段
 * @param {Object} query - 查询参数对象
 * @returns {Object} 移除后的查询参数对象
 * @example { locale: 'en_us', id: 1 } -> { id: 1 }
 */
const removeLocaleQuery = (query = {}) => {
  const nextQuery = { ...query }
  delete nextQuery[LOCALE_PARAM_KEY]
  return nextQuery
}

/**
 * 获取当前使用的语言码，按优先级自动降级匹配
 * 优先级：路径获取的语言码 > 传入的 fallback 语言码 > Storage 存储的语言码 > 传入的 browser 语言码 > 浏览器实际环境语言码 > 默认全局语言码 (zh_cn)
 * @param {Object} options - 参数对象
 * @param {string} [options.path='/'] - 尝试从中解析语言码的路由路径
 * @param {string} [options.fallbackLocale=''] - 第一后备语言码
 * @param {string} [options.storageLocale] - 从本地存储读取的语言码（不传则自带获取逻辑）
 * @param {string} [options.browserLocale] - 浏览器环境语言码（不传则自带获取逻辑）
 * @returns {string} 匹配到的或者默认的语言码
 * @example { path: '/en_us/home' } -> 'en_us'
 * @example { path: '/home', fallbackLocale: 'ja_jp' } -> 'ja_jp'
 */
const getCurrentLocale = ({
  path = '/',
  fallbackLocale = '',
  storageLocale,
  browserLocale,
} = {}) => {
  const localeFromPath = getLocaleFromPath(path)

  if (localeFromPath) {
    return localeFromPath
  }

  if (isSupportedLocale(fallbackLocale)) {
    return fallbackLocale
  }

  const resolvedStorageLocale = storageLocale ?? globalThis?.localStorage?.getItem?.('locale')
  if (isSupportedLocale(resolvedStorageLocale)) {
    return resolvedStorageLocale
  }

  if (isSupportedLocale(browserLocale)) {
    return browserLocale
  }

  if (typeof navigator !== 'undefined') {
    return borwserLang() || DEFAULT_LOCALE
  }

  return DEFAULT_LOCALE
}

/**
 * 解析并确定最终采用的语言码
 * 优先级：路径 > 查询参数 > 回退选项 > 默认值
 * @returns {string} 决定的语言码
 * @example { path: '/en_us/home' } -> 'en_us'
 */
const resolveLocale = ({
  path = '/',
  query = {},
  fallbackLocale = DEFAULT_LOCALE,
} = {}) => {
  return getLocaleFromPath(path) || query[LOCALE_PARAM_KEY] || fallbackLocale || DEFAULT_LOCALE
}

/**
 * 规范化语言路由目标对象，用于路由跳转、状态同步等
 * 会确定最终语言码、补全语言前缀、移除 query 中的 locale，并返回规范化结果
 * @param {Object} options - 待规范化的路由信息
 * @param {string} options.path - 原始路径，可为裸业务路径（如 /home）、已带语言前缀的路径（如 /en_us/home）或根路径 /
 * @param {Object} options.query - 原始查询参数
 * @param {string} options.hash - 原始 hash
 * @param {string} options.fallbackLocale - 路径和 query 均未提供语言时的回退语言
 * @returns {Object} 包含 locale、path、businessPath、query、hash 和 changed 的规范化结果
 * @example { path: '/home', query: { locale: 'en_us' } } -> { locale: 'en_us', path: '/en_us/home', businessPath: '/home', query: {}, hash: '', changed: true }
 */
const canonicalizeLocaleTarget = ({
  path = '/',
  query = {},
  hash = '',
  fallbackLocale = DEFAULT_LOCALE,
} = {}) => {
  const locale = resolveLocale({ path, query, fallbackLocale })
  const businessPath = normalizeBusinessPath(path)
  const nextPath = addLocalePrefix(businessPath, locale)
  const nextQuery = removeLocaleQuery(query)

  return {
    locale: isSupportedLocale(locale) ? locale : DEFAULT_LOCALE,
    path: nextPath,
    businessPath,
    query: nextQuery,
    hash: hash || '',
    changed: normalizePath(path) !== nextPath || Object.keys(nextQuery).length !== Object.keys(query).length,
  }
}

/**
 * 为给定的 URL 或路径添加语言前缀
 * 如果是绝对链接则直接返回
 * @param {string} url - 原始 URL 或路径
 * @param {string} locale - 语言码
 * @returns {string} 带有语言前缀的 URL
 * @example ('/trade', 'en_us') -> '/en_us/trade'
 * @example ('https://example.com', 'en_us') -> 'https://example.com'
 */
const localizeUrl = (url = '', locale = DEFAULT_LOCALE) => {
  if (!url || /^https?:\/\//.test(url)) {
    return url
  }

  const [beforeHash, hash = ''] = url.split('#')
  const [path = '/', search = ''] = beforeHash.split('?')
  const localizedPath = addLocalePrefix(path, locale)

  return `${localizedPath}${search ? `?${search}` : ''}${hash ? `#${hash}` : ''}`
}

/**
 * 解析并安全解码重定向的目标路径
 * @param {string} redirect - 重定向路径
 * @returns {string} 解码后的重定向路径，解析失败时返回原始路径
 * @example '/home%2Findex' -> '/home/index'
 */
const resolveRedirectTarget = (redirect = '') => {
  if (!redirect) {
    return ''
  }

  try {
    return decodeURIComponent(redirect)
  } catch {
    return redirect
  }
}

const buildRedirectUrl = (path = '/login', redirect = '') => {
  if (!redirect) {
    return path
  }

  const searchParams = new URLSearchParams()
  searchParams.set('redirect', redirect)
  return `${path}?${searchParams.toString()}`
}

/**
 * 封装重定向配置对象/函数，自动处理国际化前缀逻辑
 * 根据目标对象的语言动态更新重定向的 `path` 属性
 * @param {string|Object|Function} redirect - 原始路由配置中的 redirect 声明
 * @returns {Function} 可供 Vue Router 使用的 redirect 处理器方法
 * @example '/home' -> to 参数如果是 '/en_us' 将会重定向到 '/en_us/home'
 * @example { path: '/home', query: { a: 1 } } -> 重定向为带有 '/en_us/home' path 的对象
 */
const localizeRedirect = (redirect) => {
  return (to) => {
    const locale = to?.params?.locale || getLocaleFromPath(to?.path) || DEFAULT_LOCALE

    if (typeof redirect === 'function') {
      const resolvedRedirect = redirect(to)
      return localizeRedirect(resolvedRedirect)(to)
    }

    if (typeof redirect === 'string') {
      return {
        path: addLocalePrefix(redirect, locale),
        query: to?.query,
        hash: to?.hash,
        replace: true,
      }
    }

    if (redirect && typeof redirect === 'object' && redirect.path) {
      return {
        ...redirect,
        path: addLocalePrefix(redirect.path, locale),
      }
    }

    return redirect
  }
}

/**
 * 将常规路由配置项本地化，包括路径匹配和重定向等属性的处理
 * 会对所有子路由递归调这个处理
 * @param {Object} route - 原始 Vue 路由记录
 * @returns {Object} 带有语言插槽 `:locale` 和动态处理重定向的新路由记录
 * @example { path: '/home', redirect: '/index' } -> { path: '/:locale(zh_cn|en_us|...)/home', redirect: ... }
 */
const localizeRouteRecord = (route) => {
  const nextRoute = {
    ...route,
  }

  if (nextRoute.path && nextRoute.path.startsWith('/')) {
    nextRoute.path = `/:locale(${SUPPORTED_LOCALES.join('|')})${getBusinessPath(nextRoute.path)}`
  }

  if (nextRoute.redirect) {
    nextRoute.redirect = localizeRedirect(nextRoute.redirect)
  }

  if (nextRoute.children?.length) {
    nextRoute.children = nextRoute.children.map(localizeRouteRecord)
  }

  return nextRoute
}

export {
  DEFAULT_HOME_PATH,
  DEFAULT_LOCALE,
  LOCALE_PARAM_KEY,
  SUPPORTED_LOCALES,
  addLocalePrefix,
  canonicalizeLocaleTarget,
  buildRedirectUrl,
  getCurrentLocale,
  getBusinessPath,
  getLocaleFromPath,
  isLocalePath,
  isSupportedLocale,
  localizeRedirect,
  localizeRouteRecord,
  localizeUrl,
  normalizeBusinessPath,
  normalizePath,
  removeLocaleQuery,
  resolveRedirectTarget,
  resolveLocale,
  stripLocalePrefix,
}
